import ElementBound from '../core/ElementBound';
import { DataType } from 'larasnap/enums';
import { moment, numberToCurrency, currencyToNumber } from 'larasnap/support';
import merge from 'lodash-es/merge';
import isObject from 'lodash-es/isObject';

export default class extends ElementBound {
    defaultAutocompleteProps = {
        minLength: 1,
        autoFocus: false,
    };

    constructor(element, parent, props) {
        super(element);

        this.setProps(props);
        this._parent = parent;
        this._group = element.closest('.form-row');
        this._input = element.find(`:input[name="${props.name}"]`);
        this._loading = element.find('.loading-message');

        this._input.data('instance', this);
        this._input.attr('data-is-datamodel-field-instance', 'true');

        this._props.originalRequiredValue = this._props.required;

        if (this.isView()) {
            this._view = element.find('.view-value');
            // this.setViewElement();
            if (this._props.dataType === DataType.CURRENCY) {
                // This will format all currency fields with numberToCurrency
                this.setValue(this.getValue());
            }
        }

        if (this.isEdit() && this._props.autocomplete) {
            this.autocompleteInit();
        }

        if (this._group.length > 0) {
            this.setWrapper(this._group);
        } else {
            this.setWrapper(this._element);
        }
    }

    setValidationError(error) {
        this.runHandler('validation-error', error);
    }

    /****************************************************************************************/
    //                                   Get Value
    /****************************************************************************************/

    getValue() {
        let value = this._input.val();
        const dataType = this._props.dataType;

        if (value) {
            if (dataType === DataType.CURRENCY) {
                value = currencyToNumber(value);
            } else if (
                dataType === DataType.INTEGER ||
                dataType === DataType.FLOAT
            ) {
                value = Number(value);
            } else if (dataType === DataType.BOOLEAN) {
                value = Number(value) ? 1 : 0;
            }
        } else {
            if (dataType !== DataType.STRING && dataType !== DataType.JSON) {
                value = null;
            }
        }

        return value;
    }

    /****************************************************************************************/
    //                                     Set Value
    /****************************************************************************************/

    setValue(value = '') {
        if (this.isView()) {
            this.setViewValue(value);
        }

        this.setInputValue(value);
        return this;
    }

    setInputValue(value = '') {
        const dataType = this._props.dataType;

        if (value && dataType === DataType.DATE) {
            value = moment(value).format('date');
        } else if (value && dataType === DataType.DATETIME) {
            value = moment(value).format('datetime');
        }

        if (dataType === DataType.BOOLEAN) {
            value = Number(value) ? 1 : 0;
        }

        this._input.val(value);

        return this;
    }

    setViewValue(value = '') {
        if (this._props.dataType === DataType.CURRENCY) {
            const { wrapNegativesInParentheses } = this._props;
            value = numberToCurrency(currencyToNumber(value), {
                wrapNegativesInParentheses,
            });
        } else if (value && this._props.dataType === DataType.DATE) {
            value = moment(value).format('date');
        } else if (value && this._props.dataType === DataType.DATETIME) {
            value = moment(value).format('datetime');
        }

        this._view.html(value);
        return this;
    }

    clearValue() {
        this.setValue('');
    }

    /****************************************************************************************/
    //                                    Visibility
    /****************************************************************************************/

    hide(val = false) {
        return this.show(val);
    }

    hideRow(val = false) {
        return this.showRow(val);
    }

    show(val = true) {
        return this.setVisibility(val, this._wrapper);
    }

    showRow(val = true) {
        return this.setVisibility(val, this._group);
    }

    setVisibility(val = true, element) {
        if (val) {
            element.show();
            this.setRequired(this._props.originalRequiredValue);
        } else {
            element.hide();
            this.setRequired(false);
        }

        return this;
    }

    enable(val = true) {
        return this.setEnabled(val);
    }

    disable(val = true) {
        return this.setEnabled(!val);
    }

    setEnabled(isEnabled) {
        this._input.prop('disabled', !isEnabled);
        return this;
    }

    /****************************************************************************************/
    //                                   Loading Message
    /****************************************************************************************/

    showLoadingMessage(val = true) {
        if (val === false) {
            this._loading.hide();

            if (this.isView()) {
                this._view.show();
            } else {
                this._input.show();
            }
        } else {
            if (val === true) {
                val = 'Loading...';
            }

            if (this.isView()) {
                this._view.hide();
            } else {
                this._input.hide();
            }

            this._loading.html(`<i class="spinner"></i> ${val}`).show();
        }
    }

    isLoading() {
        return this._loading.is(':visible');
    }

    /****************************************************************************************/
    //                                  Auto Complete
    /****************************************************************************************/

    autocompleteInit() {
        let props;

        if (Array.isArray(this._props.autocomplete)) {
            props = merge(this.defaultAutocompleteProps, {
                source: this._props.autocomplete,
            });
        } else {
            props = merge(
                this.defaultAutocompleteProps,
                this._props.autocomplete,
            );
        }

        this._input.autocomplete(props);
        this._input.focus(function () {
            $(this).autocomplete('search', $(this).val());
        });
    }

    /****************************************************************************************/
    //                                     Support
    /****************************************************************************************/

    reload() {
        return this._parent.reload([this._props.name]);
    }

    getParent() {
        return this._parent;
    }

    setLabel(val = '') {
        if (this._group.length > 0) {
            this._group.find('.form-group-label').html(val);
        }

        return this;
    }

    getLabel() {
        if (this._group.length > 0) {
            return this._group.find('.form-group-label').html();
        }
    }

    hasRow() {
        return this._element.closest('.form-row').length;
    }

    setRequired(isRequired = true, message = null) {
        this._props.required = isRequired;
        this._props.requiredMessage = message;
        return this;
    }

    isHidden() {
        return this._props.displayType === 'hidden';
    }

    isView() {
        return this._props.displayType === 'view';
    }

    isEdit() {
        return this._props.displayType === 'edit';
    }

    getInput() {
        return this._input;
    }

    hasInput() {
        return !!this._input;
    }

    on(events, func) {
        if (this.hasInput()) {
            return this._input.on(events, func);
        }
    }

    change(func = null) {
        if (this.hasInput()) {
            if (!func) {
                this._input.change();
            } else {
                return this._input.change(func);
            }
        }
    }

    showHelperMessage(message = null) {
        if (message) {
            this.find('.helper-message')
                .children()
                .attr('data-content', message);
        }

        this.find('.helper-message').show();
    }

    hideHelperMessage() {
        this.find('.helper-message').hide();
    }

    normalizeOptions(options = {}, labelField = null, valueField = null) {
        if (Array.isArray(options) && labelField && valueField) {
            return options.map(option => {
                if (isObject(option)) {
                    return {
                        value: option[valueField],
                        label: option[labelField],
                    };
                } else {
                    return { value: option, label: option };
                }
            });
        } else if (Array.isArray(options)) {
            return options.map(option => ({ label: option, value: option }));
        } else if (!Array.isArray(options)) {
            return Object.entries(options).map(([value, label]) => ({
                value,
                label,
            }));
        } else {
            return options;
        }
    }
}
